import React, { useEffect, useState } from 'react';
import { fastapiService } from '../../services/fastapiService';
import { WorkspaceEmailProviderModal } from '../../components/workspaces/WorkspaceEmailProviderModal';
import { WorkspaceAddCreditsModal } from '../../components/workspaces/WorkspaceAddCreditsModal';
import { Mail, Coins } from 'lucide-react';

export const WorkspacesPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [emailProviderWorkspace, setEmailProviderWorkspace] = useState<{ id: string; name: string } | null>(null);
  const [addCreditsWorkspace, setAddCreditsWorkspace] = useState<{ id: string; name: string } | null>(null);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        setLoading(true);
        setError('');
        const params: any = { page, limit };
        if (search) params.search = search;
        const res = await fastapiService.getWorkspaces(params);
        setWorkspaces(res.data || []);
        setTotal(res.pagination?.total || 0);
      } catch (e: any) {
        setError(e?.message || 'Failed to load workspaces');
      } finally {
        setLoading(false);
      }
    };
    fetchWorkspaces();
  }, [page, limit, search]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Workspaces</h1>
        <p className="mt-1 text-sm text-admin-600">
          Manage workspaces and their configurations
        </p>
      </div>
      
      <div className="admin-card p-6">
        <div className="flex items-center justify-between mb-4">
          <input
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            placeholder="Search workspaces..."
            className="admin-input w-full max-w-xs"
          />
        </div>

        {error && (
          <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        {loading ? (
          <p className="text-admin-500">Loading...</p>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-admin-200">
                <thead className="bg-admin-50">
                  <tr className="text-left text-xs font-medium uppercase tracking-wider text-admin-500">
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Owner</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-admin-200">
                  {workspaces.map((w: any) => (
                    <tr key={w.id} className="hover:bg-admin-50">
                      <td className="px-4 py-4 text-sm font-medium text-admin-900">{w.name || '-'}</td>
                      <td className="px-4 py-4 text-sm text-admin-700">
                        {w.owner?.email || w.owner?.full_name || '-'}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          w.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-admin-100 text-admin-700'
                        }`}>
                          {w.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-admin-500">
                        {w.created_at ? new Date(w.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setAddCreditsWorkspace({ id: w.id, name: w.name || 'Workspace' })}
                            className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700"
                            title="Add credits to workspace"
                          >
                            <Coins className="w-4 h-4" />
                            Credits
                          </button>
                          <button
                            onClick={() => setEmailProviderWorkspace({ id: w.id, name: w.name || 'Workspace' })}
                            className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700"
                            title="Configure email sender (Resend/Mailgun)"
                          >
                            <Mail className="w-4 h-4" />
                            Email
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {workspaces.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-admin-500">
                        No workspaces found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm text-admin-500">
                Page {page} of {totalPages} • {total} total
              </div>
              <div className="flex items-center gap-2">
                <button className="admin-button h-8 px-3" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                  Prev
                </button>
                <button className="admin-button h-8 px-3" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {emailProviderWorkspace && (
        <WorkspaceEmailProviderModal
          workspaceId={emailProviderWorkspace.id}
          workspaceName={emailProviderWorkspace.name}
          isOpen={!!emailProviderWorkspace}
          onClose={() => setEmailProviderWorkspace(null)}
        />
      )}
      {addCreditsWorkspace && (
        <WorkspaceAddCreditsModal
          workspaceId={addCreditsWorkspace.id}
          workspaceName={addCreditsWorkspace.name}
          isOpen={!!addCreditsWorkspace}
          onClose={() => setAddCreditsWorkspace(null)}
          onSaved={() => {}}
        />
      )}
    </div>
  );
};

